import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Card, Spinner, OverlayTrigger, Tooltip, Dropdown, Form, Button, Row, Col } from 'react-bootstrap'
import { stockItemAPI } from '../api/stockItemAPI'
import { vendorAPI } from '../api/vendorAPI'
import { highlightText } from '../utils/commonUtils'
import ResizableTable from '../components/ResizableTable'
import PaginationComponent from '../components/Pagination'
import { getCountryFlag } from '../utils/tableUtils'
import { useSearchWithDebounce } from '../hooks/useSearchWithDebounce'

function OutOfStockItemsWidget({ onDataUpdate, restoreState, onStateRestored }) {
    const navigate = useNavigate()
    const location = useLocation()
    
    // State for stock items and pagination
    const [stockItems, setStockItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const [totalPages, setTotalPages] = useState(0)
    
    // State for vendor mapping
    const [vendorMap, setVendorMap] = useState({})
    
    // State for period days selection
    const [periodDays, setPeriodDays] = useState(60)

    // State for manufactured/purchased filter
    const [manufacturedFilter, setManufacturedFilter] = useState('')

    // Search functionality with debouncing
    const { searchTerm, searchInputValue, handleSearchInputChange, clearSearch } = useSearchWithDebounce('')

    // Period days options
    const periodDaysOptions = [
        { label: 'All Out of Stock Items', value: 0 },
        { label: 'Recently Active Out of Stock', value: 60 },
        { label: 'Almost Out of Stock Items', value: -1 }
    ]
    
    // Bulk selection state
    const [selectedRows, setSelectedRows] = useState([])
    const [selectAll, setSelectAll] = useState(false)
    

    // Handle period days change
    const handlePeriodDaysChange = (selectedOption) => {
        setPeriodDays(selectedOption.value);
        setCurrentPage(1); // Reset to first page when changing period
    }

    // Fetch out of stock items
    const fetchOutOfStockItems = useCallback(async () => {
        try {
            setLoading(true);
            let response;

            if (periodDays === -1) {
                // Fetch almost out of stock items
                response = await stockItemAPI.getAlmostOutOfStock(currentPage, pageSize, searchTerm, manufacturedFilter || undefined);
            } else {
                // Fetch regular out of stock items
                response = await stockItemAPI.getOutOfStock(currentPage, pageSize, periodDays, searchTerm, manufacturedFilter || undefined);
            }

            console.log('Stock items API response:', response);

            const data = response.results;

            if (!Array.isArray(data)) {
                throw new Error('Expected an array of stock items');
            }
            setStockItems(data);
            setTotalPages(response.count);
        } catch (error) {
            console.error('Error fetching stock items:', error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, periodDays, searchTerm, manufacturedFilter])

    // Fetch vendors for manufacturer mapping
    useEffect(() => {
        async function fetchVendors() {
            try {
                const vendors = await vendorAPI.getAll()
                const mapping = vendors.reduce((acc, vendor) => {
                    acc[vendor._id] = vendor.name
                    return acc
                }, {})
                setVendorMap(mapping)
            } catch (error) {
                console.error('Error fetching vendors:', error)
            }
        }
        fetchVendors()
    }, [])

    // Fetch out of stock items when page or page size changes
    useEffect(() => {
        fetchOutOfStockItems()
    }, [fetchOutOfStockItems])

    // Reset to page 1 when search term changes
    useEffect(() => {
        setCurrentPage(1)
    }, [searchTerm])

    // Handler for data refresh
    const handleDataRefresh = useCallback(() => {
        fetchOutOfStockItems()
    }, [fetchOutOfStockItems])

    // Handle page change
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Selection handlers following InventoryListComponent pattern
    const handleRowSelect = (row, isSelected) => {
        if (isSelected) {
            const selectedItem = {
                id: row._id,
                description: row.description,
                manufacturer: row.manufacturer,
                manufacturer_name: vendorMap[row.manufacturer] || row.manufacturer || 'Unknown',
                gtin: row.gtin,
                sku: row.sku,
                image: row.image,
                total_inventory: row.total_inventory,
                critical_stock_level: row.critical_stock_level
            }
            setSelectedRows(prev => [...prev, selectedItem])
        } else {
            setSelectedRows(prev => prev.filter(item => item.id !== row._id))
        }
    }

    const handleSelectAll = (isSelected) => {
        setSelectAll(isSelected)
        if (isSelected) {
            const allSelectableRows = stockItems.map(item => ({
                id: item._id,
                description: item.description,
                manufacturer: item.manufacturer,
                manufacturer_name: vendorMap[item.manufacturer] || item.manufacturer || 'Unknown',
                gtin: item.gtin,
                sku: item.sku,
                image: item.image,
                total_inventory: item.total_inventory,
                critical_stock_level: item.critical_stock_level
            }))
            setSelectedRows(allSelectableRows)
        } else {
            setSelectedRows([])
        }
    }

    const isRowSelected = (rowId) => {
        return selectedRows.some(item => item.id === rowId)
    }

    // Clear selection when data changes
    useEffect(() => {
        setSelectedRows([])
        setSelectAll(false)
    }, [stockItems])

    // Track if we're in the middle of state restoration
    const [isRestoringState, setIsRestoringState] = useState(false)

    // Handle widget state restoration when returning from PO creation
    useEffect(() => {
        if (restoreState) {
            console.log('Restoring OutOfStockItemsWidget state:', restoreState);
            setIsRestoringState(true);
            
            // Restore widget state
            if (restoreState.periodDays !== undefined) {
                setPeriodDays(restoreState.periodDays);
            }
            if (restoreState.currentPage !== undefined) {
                setCurrentPage(restoreState.currentPage);
            }
            if (restoreState.pageSize !== undefined) {
                setPageSize(restoreState.pageSize);
            }
            if (restoreState.manufacturedFilter !== undefined) {
                setManufacturedFilter(restoreState.manufacturedFilter);
            }
            
            // Notify dashboard that state has been restored
            if (onStateRestored) {
                onStateRestored();
            }
        }
    }, [restoreState, onStateRestored])

    // Trigger data fetch after state restoration is complete
    useEffect(() => {
        if (isRestoringState) {
            console.log('State restoration complete, triggering data fetch');
            setIsRestoringState(false);
            fetchOutOfStockItems();
        }
    }, [isRestoringState, fetchOutOfStockItems])



    // Define table columns (memoized to update when searchTerm or vendorMap changes)
    const columns = useMemo(() => [
        {
            id: 'selection',
            header: () => (
                <div 
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    role="button"
                    tabIndex={-1}
                >
                    <Form.Check
                        type="checkbox"
                        checked={selectAll}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        label=""
                    />
                </div>
            ),
            cell: (row) => (
                <div 
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    role="button"
                    tabIndex={-1}
                >
                    <Form.Check
                        type="checkbox"
                        checked={isRowSelected(row._id)}
                        onChange={(e) => {
                            handleRowSelect(row, e.target.checked);
                        }}
                        label=""
                    />
                </div>
            )
        },
        {
            id: 'image',
            header: 'Image',
            cell: (row) => row.image && (
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip>
                            <img 
                                src={row.image} 
                                alt="Stock Item"
                                style={{
                                    width: '300px',
                                    height: '300px',
                                    objectFit: 'contain',
                                    border: '2px solid #122A5A',
                                    borderRadius: '4px',
                                    boxShadow: '0 4px 8px rgba(18, 42, 90, 0.1)',
                                    transition: 'transform 0.2s ease-in-out',
                                }}
                            />
                        </Tooltip>
                    }
                >
                    <img 
                        src={row.image} 
                        alt="Stock Item Thumbnail"
                        style={{
                            width: '50px',
                            height: '50px',
                            objectFit: 'contain'
                        }}
                    />
                </OverlayTrigger>
            )
        },
        {
            id: 'gtin',
            header: 'GTIN',
            cell: (row) => highlightText(row.gtin, searchTerm)
        },
        {
            id: 'sku',
            header: 'SKU',
            cell: (row) => highlightText(row.sku, searchTerm)
        },
        {
            id: 'description',
            header: 'Description',
            cell: (row) => (
                <>
                    {highlightText(row.description, searchTerm)}
                    {!row.is_active && (
                        <span className="ms-2 text-muted fst-italic">
                            (Inactive)
                        </span>
                    )}
                </>
            )
        },
        {
            id: 'manufacturer',
            header: 'Manufacturer',
            cell: (row) => highlightText(vendorMap[row.manufacturer] || row.manufacturer || 'N/A', searchTerm)
        },
        {
            id: 'countryOfOrigin',
            header: 'Country of Origin',
            cell: (row) => row.country_of_origin && (
                <>
                    {getCountryFlag(row.country_of_origin)}{' '}
                    {row.country_of_origin}{' '}
                </>
            )
        },
        {
            id: 'currentInventory',
            header: 'Current Stock',
            cell: (row) => {
                const quantity = row.total_inventory || 0;
                const critical = row.critical_stock_level;
                
                // Color logic: Red for 0, gradient to yellow for higher numbers
                let backgroundColor = '#dc3545'; // Red for 0
                let textColor = 'white';
                
                if (quantity > 0 && critical) {
                    // Calculate color based on ratio to critical level
                    const ratio = Math.min(quantity / critical, 1);
                    const red = Math.round(220 * (1 - ratio) + 255 * ratio);
                    const green = Math.round(53 * (1 - ratio) + 215 * ratio);
                    const blue = Math.round(69 * (1 - ratio) + 0 * ratio);
                    backgroundColor = `rgb(${red}, ${green}, ${blue})`;
                    
                    // Adjust text color for readability
                    textColor = ratio > 0.5 ? 'black' : 'white';
                } else if (quantity > 0) {
                    // Default yellow for items without critical level set
                    backgroundColor = '#ffc107';
                    textColor = 'black';
                }
                
                return (
                    <span 
                        style={{ 
                            backgroundColor, 
                            color: textColor,
                            padding: '2px 6px',
                            borderRadius: '3px',
                            fontWeight: 'bold',
                            fontSize: '0.85em'
                        }}
                    >
                        {quantity}
                    </span>
                );
            }
        }
    ], [searchTerm, vendorMap, selectAll, selectedRows]);

    return (
        <Card className="h-100">
            <Card.Header>
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <div style={{ position: 'relative', width: '250px' }}>
                        <Form.Control
                            type="text"
                            placeholder="Search stock items..."
                            value={searchInputValue}
                            onChange={handleSearchInputChange}
                            style={{ paddingRight: searchInputValue ? '30px' : '12px' }}
                            autoComplete="off"
                            spellCheck="false"
                        />
                        {searchInputValue && (
                            <Button
                                variant="link"
                                onClick={clearSearch}
                                title="Clear search"
                                style={{
                                    position: 'absolute',
                                    right: '2px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    padding: '0',
                                    width: '24px',
                                    height: '24px',
                                    border: 'none',
                                    background: 'transparent',
                                    color: '#6c757d',
                                    fontSize: '20px',
                                    lineHeight: '1',
                                    cursor: 'pointer',
                                    textDecoration: 'none'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.color = '#000'}
                                onMouseOut={(e) => e.currentTarget.style.color = '#6c757d'}
                            >
                                ×
                            </Button>
                        )}
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                        <Dropdown>
                            <Dropdown.Toggle
                                variant="light"
                                id="period-days-dropdown"
                                style={{
                                    fontWeight: 'bold',
                                    padding: '0rem 0.5rem 0.35rem',
                                    height: '25px'
                                }}
                            >
                                {periodDaysOptions.find(option => option.value === periodDays)?.label || 'Recently Active Items'}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                {periodDaysOptions.map((option) => (
                                    <Dropdown.Item
                                        key={option.value}
                                        onClick={() => handlePeriodDaysChange(option)}
                                        active={option.value === periodDays}
                                    >
                                        {option.label}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                        <Dropdown>
                            <Dropdown.Toggle
                                variant="light"
                                id="manufactured-filter-dropdown"
                                style={{
                                    fontWeight: 'bold',
                                    padding: '0rem 0.5rem 0.35rem',
                                    height: '25px'
                                }}
                            >
                                {manufacturedFilter === '' ? 'All Items' :
                                 manufacturedFilter === 'true' ? 'Manufactured' : 'Purchased'}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item as="button" onClick={() => { setManufacturedFilter(''); setCurrentPage(1); }}>
                                    <Form.Check
                                        type="checkbox"
                                        label="All"
                                        checked={manufacturedFilter === ''}
                                        onChange={() => { setManufacturedFilter(''); setCurrentPage(1); }}
                                    />
                                </Dropdown.Item>
                                <Dropdown.Item as="button" onClick={() => { setManufacturedFilter('true'); setCurrentPage(1); }}>
                                    <Form.Check
                                        type="checkbox"
                                        label="Manufactured"
                                        checked={manufacturedFilter === 'true'}
                                        onChange={() => { setManufacturedFilter('true'); setCurrentPage(1); }}
                                    />
                                </Dropdown.Item>
                                <Dropdown.Item as="button" onClick={() => { setManufacturedFilter('false'); setCurrentPage(1); }}>
                                    <Form.Check
                                        type="checkbox"
                                        label="Purchased"
                                        checked={manufacturedFilter === 'false'}
                                        onChange={() => { setManufacturedFilter('false'); setCurrentPage(1); }}
                                    />
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>
            </Card.Header>
            <Card.Body className="p-0">
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                    </div>
                ) : (
                    <>
                        {/* Bulk Actions for Purchase Order Creation */}
                        {selectedRows.length > 0 && (
                            <div className="px-3 py-2 border-bottom">
                                <div className="d-flex align-items-center bg-body-secondary p-2 rounded">
                                    <span className="me-3 text-body">
                                        <strong>{selectedRows.length}</strong> item{selectedRows.length !== 1 ? 's' : ''} selected
                                        {(() => {
                                            // Validate manufacturer consistency
                                            const manufacturers = [...new Set(selectedRows.map(item => item.manufacturer))]
                                            if (manufacturers.length === 1 && manufacturers[0]) {
                                                const manufacturerName = selectedRows[0].manufacturer_name
                                                return <span className="ms-2">from <strong>{manufacturerName}</strong></span>
                                            } else if (manufacturers.length > 1) {
                                                return <span className="ms-2 text-danger">(Mixed manufacturers - cannot create PO)</span>
                                            }
                                            return null
                                        })()}
                                    </span>
                                    {(() => {
                                        // Only show Create PO button if all items are from same manufacturer
                                        const manufacturers = [...new Set(selectedRows.map(item => item.manufacturer))]
                                        if (manufacturers.length === 1 && manufacturers[0]) {
                                            return (
                                                <Button 
                                                    variant="success" 
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => {
                                                        // Navigate to PO creation with pre-selected items and return path info
                                                        navigate('/purchase-order/create', {
                                                            state: {
                                                                preSelectedItems: selectedRows,
                                                                manufacturer: {
                                                                    id: selectedRows[0].manufacturer,
                                                                    name: selectedRows[0].manufacturer_name
                                                                },
                                                                returnToWidget: {
                                                                    path: '/dashboard',
                                                                    widgetType: 'OutOfStockItems',
                                                                    widgetState: {
                                                                        periodDays: periodDays,
                                                                        currentPage: currentPage,
                                                                        pageSize: pageSize,
                                                                        manufacturedFilter: manufacturedFilter
                                                                    }
                                                                }
                                                            }
                                                        })
                                                    }}
                                                >
                                                    <i className="material-icons" style={{fontSize: '14px', marginRight: '4px'}}>shopping_cart</i>
                                                    Create Purchase Order
                                                </Button>
                                            )
                                        }
                                        return null
                                    })()}
                                    <Button 
                                        variant="outline-danger" 
                                        size="sm" 
                                        onClick={() => {
                                            setSelectedRows([])
                                            setSelectAll(false)
                                        }}
                                    >
                                        Clear Selection
                                    </Button>
                                </div>
                            </div>
                        )}
                        <div style={{ overflowX: 'auto', minWidth: '100%' }}>
                            <ResizableTable
                                columns={columns}
                                data={stockItems}
                                onRowClick={null}
                                initialColumnWidths={{
                                    selection: '60px',
                                    image: 'auto',
                                    gtin: 'auto',
                                    sku: 'auto',
                                    description: 300,
                                    manufacturer: 'auto',
                                    countryOfOrigin: 120,
                                    currentInventory: 100
                                }}
                                tableId="out_of_stock_items"
                                striped={true}
                                hover={true}
                                className="table-sm"
                            />
                        </div>
                        <PaginationComponent
                            currentPage={currentPage}
                            totalItems={totalPages}
                            onPageChange={handlePageChange}
                            onItemsPerPageChange={setPageSize}
                        />
                    </>
                )}
            </Card.Body>
        </Card>
    )
}

export default OutOfStockItemsWidget
